/**
 * Utility functions for handling automatic break times
 * Automatic break is from 12:00-13:00 Romanian time (Europe/Bucharest) every day
 * Updated to use server time synchronization for consistency
 */

import { timeService } from '../services/timeService';

/**
 * Check if the current Romanian time is within the automatic break period (12:00-13:00)
 * Uses server-synchronized time for consistency across all devices
 */
export const isInAutomaticBreakTime = (): boolean => {
  try {
    // Use server-synchronized Romanian time
    const romanianTime = timeService.getCurrentRomanianTime();
    const hour = romanianTime.getHours();
    return hour >= 12 && hour < 13; // 12:00-12:59
  } catch (error) {
    console.error('BreakUtils: Failed to get server time, falling back to client time:', error);
    // Fallback to client time calculation
    const now = new Date();
    const romanianTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Bucharest" }));
    const hour = romanianTime.getHours();
    return hour >= 12 && hour < 13;
  }
};

/**
 * Get the next automatic break start time (12:00 Romanian time)
 */
export const getNextBreakStartTime = (): Date => {
  try {
    const romanianTime = timeService.getCurrentRomanianTime();
    
    // Create break start time for today at 12:00
    const breakStart = new Date(romanianTime);
    breakStart.setHours(12, 0, 0, 0);
    
    // If it's already past 12:00 today, set it for tomorrow
    if (romanianTime.getHours() >= 12) {
      breakStart.setDate(breakStart.getDate() + 1);
    }
    
    return breakStart;
  } catch (error) {
    console.error('BreakUtils: Failed to get server time for break start, falling back to client time:', error);
    const now = new Date();
    const romanianTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Bucharest" }));
    const breakStart = new Date(romanianTime);
    breakStart.setHours(12, 0, 0, 0);
    if (romanianTime.getHours() >= 12) {
      breakStart.setDate(breakStart.getDate() + 1);
    }
    return breakStart;
  }
};

/**
 * Get the next automatic break end time (13:00 Romanian time)
 */
export const getNextBreakEndTime = (): Date => {
  try {
    const romanianTime = timeService.getCurrentRomanianTime();
    
    // Create break end time for today at 13:00
    const breakEnd = new Date(romanianTime);
    breakEnd.setHours(13, 0, 0, 0);
    
    // If it's already past 13:00 today, set it for tomorrow
    if (romanianTime.getHours() >= 13) {
      breakEnd.setDate(breakEnd.getDate() + 1);
    }
    
    return breakEnd;
  } catch (error) {
    console.error('BreakUtils: Failed to get server time for break end, falling back to client time:', error);
    const now = new Date();
    const romanianTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Bucharest" }));
    const breakEnd = new Date(romanianTime);
    breakEnd.setHours(13, 0, 0, 0);
    if (romanianTime.getHours() >= 13) {
      breakEnd.setDate(breakEnd.getDate() + 1);
    }
    return breakEnd;
  }
};

/**
 * Get minutes remaining until break starts or ends
 */
export const getMinutesUntilBreakChange = (): { type: 'start' | 'end'; minutes: number } => {
  const now = new Date();
  const romanianTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Bucharest" }));
  
  if (isInAutomaticBreakTime()) {
    // Currently in break, return minutes until break ends
    const breakEnd = getNextBreakEndTime();
    if (romanianTime.getHours() === 12) {
      // Break ends at 13:00 today
      breakEnd.setDate(romanianTime.getDate());
    }
    const minutes = Math.floor((breakEnd.getTime() - romanianTime.getTime()) / (1000 * 60));
    return { type: 'end', minutes };
  } else {
    // Not in break, return minutes until break starts
    const breakStart = getNextBreakStartTime();
    const minutes = Math.floor((breakStart.getTime() - romanianTime.getTime()) / (1000 * 60));
    return { type: 'start', minutes };
  }
};

/**
 * Format break status message for display
 */
export const getBreakStatusMessage = (): string => {
  const { type, minutes } = getMinutesUntilBreakChange();
  
  if (type === 'end') {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `Pauza se termină în ${hours}h ${remainingMinutes}m`;
    } else {
      return `Pauza se termină în ${minutes} minute`;
    }
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `Pauza începe în ${hours}h ${remainingMinutes}m`;
    } else if (minutes > 60) {
      return `Pauza începe în ${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    } else {
      return `Pauza începe în ${minutes} minute`;
    }
  }
};

/**
 * Check if a user should be automatically on break based on their work status
 */
export const shouldUserBeOnAutomaticBreak = (isWorking: boolean): boolean => {
  return isWorking && isInAutomaticBreakTime();
};